import { useEffect, useState } from 'react';

interface WebhookStatus {
  configured: boolean;
  masked: string | null;
}

interface NotifyEvents {
  blocked: boolean;
  stalled: boolean;
  deploy_failed: boolean;
  pr_holding: boolean;
}

interface NotificationsSettings {
  teams: WebhookStatus;
  slack: WebhookStatus;
  events: NotifyEvents;
  digest_enabled: boolean;
  digest_hour: number;
}

const EVENT_LABELS: { key: keyof NotifyEvents; label: string }[] = [
  { key: 'blocked', label: 'Track blocked on human decision' },
  { key: 'stalled', label: 'Track stalled' },
  { key: 'deploy_failed', label: 'Deploy failed' },
  { key: 'pr_holding', label: 'PR holding for merge' },
];

async function fetchJson<T>(path: string): Promise<T> {
  const res = await fetch(path);
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) throw new Error(body?.error ?? `request failed: ${res.status}`);
  return body as T;
}

async function putJson<T>(path: string, payload: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) throw new Error(body?.error ?? `request failed: ${res.status}`);
  return body as T;
}

async function postJson(path: string): Promise<void> {
  const res = await fetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) throw new Error(body?.error ?? `request failed: ${res.status}`);
}

function WebhookField({
  label,
  status,
  value,
  onChange,
  onClear,
}: {
  label: string;
  status: WebhookStatus;
  value: string | null;
  onChange: (v: string) => void;
  onClear: () => void;
}) {
  return (
    <div style={{ maxWidth: 380, marginBottom: 12 }}>
      <label className="card-sub" style={{ display: 'block', marginBottom: 4 }}>
        {label}
        {status.configured && <span className="badge badge-green" style={{ marginLeft: 8 }}>configured</span>}
      </label>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          type="url"
          placeholder={status.configured ? (status.masked ?? 'configured') : 'https://…'}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          style={{ flex: 1, minWidth: 0 }}
        />
        <button className="btn btn-outline" type="button" onClick={onClear}>
          Clear
        </button>
      </div>
    </div>
  );
}

function NotificationsSection() {
  const [settings, setSettings] = useState<NotificationsSettings | null>(null);
  const [teamsValue, setTeamsValue] = useState<string | null>(null);
  const [slackValue, setSlackValue] = useState<string | null>(null);
  const [events, setEvents] = useState<NotifyEvents | null>(null);
  const [digestEnabled, setDigestEnabled] = useState<boolean | null>(null);
  const [digestHour, setDigestHour] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchJson<NotificationsSettings>('/api/settings/notifications')
      .then((r) => {
        setSettings(r);
        setEvents(r.events);
        setDigestEnabled(r.digest_enabled);
        setDigestHour(r.digest_hour);
      })
      .catch((e) => setError((e as Error).message));
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  function toggleEvent(key: keyof NotifyEvents) {
    if (!events) return;
    setEvents({ ...events, [key]: !events[key] });
  }

  async function save() {
    if (!events || busy) return;
    setBusy(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = { events };
      if (teamsValue !== null) payload.teams_webhook_url = teamsValue;
      if (slackValue !== null) payload.slack_webhook_url = slackValue;
      if (digestEnabled !== null) payload.digest_enabled = digestEnabled;
      if (digestHour !== null) payload.digest_hour = digestHour;
      const result = await putJson<NotificationsSettings>('/api/settings/notifications', payload);
      setSettings(result);
      setEvents(result.events);
      setDigestEnabled(result.digest_enabled);
      setDigestHour(result.digest_hour);
      setTeamsValue(null);
      setSlackValue(null);
      setToast('Saved');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function sendTest() {
    if (testBusy) return;
    setTestBusy(true);
    setError(null);
    try {
      await postJson('/api/settings/notifications/test');
      setToast('Test notification sent');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setTestBusy(false);
    }
  }

  if (!settings || !events || digestEnabled === null || digestHour === null) {
    return error ? <div className="errors">{error}</div> : <div className="empty">Loading…</div>;
  }

  return (
    <div className="section">
      <h3>Notifications</h3>
      {error && <div className="errors">{error}</div>}
      {toast && <div className="badge badge-green" style={{ marginBottom: 12 }}>{toast}</div>}

      <WebhookField
        label="Teams webhook URL"
        status={settings.teams}
        value={teamsValue}
        onChange={setTeamsValue}
        onClear={() => setTeamsValue('')}
      />
      <WebhookField
        label="Slack webhook URL"
        status={settings.slack}
        value={slackValue}
        onChange={setSlackValue}
        onClear={() => setSlackValue('')}
      />

      <div style={{ marginBottom: 12 }}>
        {EVENT_LABELS.map(({ key, label }) => (
          <label
            key={key}
            className="card-sub"
            style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginBottom: 4 }}
          >
            <input type="checkbox" checked={events[key]} onChange={() => toggleEvent(key)} />
            {label}
          </label>
        ))}
      </div>

      <div style={{ marginBottom: 12 }}>
        <label
          className="card-sub"
          style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginBottom: 4 }}
        >
          <input
            type="checkbox"
            checked={digestEnabled}
            onChange={() => setDigestEnabled(!digestEnabled)}
          />
          Daily digest
        </label>
        <label className="card-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          Send at (ET hour)
          <input
            type="number"
            min={0}
            max={23}
            step={1}
            value={digestHour}
            disabled={!digestEnabled}
            onChange={(e) => {
              const next = Number(e.target.value);
              if (Number.isInteger(next) && next >= 0 && next <= 23) setDigestHour(next);
            }}
            style={{ width: 60 }}
          />
        </label>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary" type="button" disabled={busy} onClick={save}>
          Save
        </button>
        <button className="btn btn-outline" type="button" disabled={testBusy} onClick={sendTest}>
          Send test
        </button>
      </div>
    </div>
  );
}

function AutomationToggle() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchJson<{ enabled: boolean }>('/api/settings/audit-automation')
      .then((r) => setEnabled(r.enabled))
      .catch(() => setEnabled(false));
  }, []);

  async function toggle() {
    if (enabled === null || busy) return;
    const prev = enabled;
    const next = !enabled;
    setEnabled(next);
    setBusy(true);
    try {
      const res = await fetch('/api/settings/audit-automation', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: next }),
      });
      if (!res.ok) throw new Error(`request failed: ${res.status}`);
    } catch {
      setEnabled(prev);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="badge-row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
      <label
        className="card-sub"
        style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: enabled === null || busy ? 'default' : 'pointer' }}
      >
        <input type="checkbox" checked={enabled ?? false} disabled={enabled === null || busy} onChange={toggle} />
        Automatic audits
      </label>
      {enabled === false && <span className="card-sub">on-demand only</span>}
    </div>
  );
}

function AutopilotToggle() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchJson<{ enabled: boolean }>('/api/settings/autopilot')
      .then((r) => setEnabled(r.enabled))
      .catch(() => setEnabled(false));
  }, []);

  async function toggle() {
    if (enabled === null || busy) return;
    const prev = enabled;
    const next = !enabled;
    setEnabled(next);
    setBusy(true);
    try {
      const res = await fetch('/api/settings/autopilot', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: next }),
      });
      if (!res.ok) throw new Error(`request failed: ${res.status}`);
    } catch {
      setEnabled(prev);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="badge-row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
      <label
        className="card-sub"
        style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: enabled === null || busy ? 'default' : 'pointer' }}
      >
        <input type="checkbox" checked={enabled ?? false} disabled={enabled === null || busy} onChange={toggle} />
        Autopilot (Architect auto-approve)
      </label>
      {enabled === false && <span className="card-sub">drafted ideas await human review</span>}
    </div>
  );
}

function LlmBudgetControl() {
  const [budget, setBudget] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchJson<{ budget_usd: number | null }>('/api/settings/llm-budget')
      .then((r) => setBudget(r.budget_usd))
      .catch(() => setBudget(null));
  }, []);

  async function update(next: number) {
    if (busy) return;
    const prev = budget;
    setBudget(next);
    setBusy(true);
    try {
      const res = await fetch('/api/settings/llm-budget', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ budget_usd: next }),
      });
      if (!res.ok) throw new Error(`request failed: ${res.status}`);
    } catch {
      setBudget(prev);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="badge-row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
      <label className="card-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        Daily LLM budget ($)
        <input
          type="number"
          min={0}
          max={500}
          step={1}
          value={budget ?? ''}
          disabled={busy}
          onChange={(e) => {
            const next = Number(e.target.value);
            if (Number.isFinite(next)) update(next);
          }}
          style={{ width: 80 }}
        />
      </label>
      <span className="card-sub">0 disables reviews</span>
    </div>
  );
}

function EngineeringSection() {
  return (
    <div className="section">
      <h3>Engineering</h3>
      <AutomationToggle />
      <AutopilotToggle />
      <LlmBudgetControl />
    </div>
  );
}

function ForemanToggle({ path, label, hint }: { path: string; label: string; hint: string }) {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchJson<{ enabled: boolean }>(path)
      .then((r) => setEnabled(r.enabled))
      .catch(() => setEnabled(false));
  }, [path]);

  async function toggle() {
    if (enabled === null || busy) return;
    const prev = enabled;
    const next = !enabled;
    setEnabled(next);
    setBusy(true);
    try {
      const res = await fetch(path, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: next }),
      });
      if (!res.ok) throw new Error(`request failed: ${res.status}`);
    } catch {
      setEnabled(prev);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="badge-row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
      <label
        className="card-sub"
        style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: enabled === null || busy ? 'default' : 'pointer' }}
      >
        <input type="checkbox" checked={enabled ?? false} disabled={enabled === null || busy} onChange={toggle} />
        {label}
      </label>
      <span className="card-sub">{hint}</span>
    </div>
  );
}

async function postJsonBody<T>(path: string, payload: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) throw new Error(body?.error ?? `request failed: ${res.status}`);
  return body as T;
}

interface ForemanDecision {
  id: string;
  created_at: string;
  track_id: string;
  condition: string;
  action: string;
  reason: string;
  outcome: string | null;
  outcome_at: string | null;
  operator_override: boolean;
}

function ForemanDecisions() {
  const [decisions, setDecisions] = useState<ForemanDecision[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function load() {
    fetchJson<{ decisions: ForemanDecision[] }>('/api/foreman/decisions')
      .then((r) => setDecisions(r.decisions))
      .catch((e) => setError((e as Error).message));
  }

  useEffect(load, []);

  async function feedback(id: string, verdict: 'good' | 'wrong') {
    if (busyId) return;
    setBusyId(id);
    setError(null);
    try {
      await postJsonBody('/api/foreman/feedback', { id, verdict });
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  if (decisions === null) {
    return error ? <div className="errors">{error}</div> : <div className="card-sub">Loading decisions…</div>;
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div className="card-sub" style={{ marginBottom: 6 }}>Recent decisions</div>
      {error && <div className="errors">{error}</div>}
      {decisions.length === 0 && <div className="card-sub">No decisions recorded yet.</div>}
      {decisions.map((d) => (
        <div key={d.id} style={{ borderTop: '1px solid var(--gray-bg)', padding: '6px 0' }}>
          <div className="badge-row" style={{ marginBottom: 2 }}>
            <span className="badge badge-gray">{d.condition}</span>
            <span className="badge badge-yellow">{d.action}</span>
            {d.outcome === 'good' && <span className="badge badge-green">good</span>}
            {d.outcome === 'overridden' && <span className="badge badge-red">wrong</span>}
            {d.operator_override && <span className="badge badge-gray">operator</span>}
            <span className="card-sub">{new Date(d.created_at).toLocaleString()}</span>
          </div>
          <div className="card-sub" style={{ marginBottom: 4 }}>{d.reason}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              className="btn btn-outline"
              type="button"
              disabled={busyId !== null}
              onClick={() => feedback(d.id, 'good')}
            >
              Good
            </button>
            <button
              className="btn btn-outline"
              type="button"
              disabled={busyId !== null}
              onClick={() => feedback(d.id, 'wrong')}
            >
              Wrong
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// Line-level diff of the proposed doctrine against the current one: proposed
// lines not present today render as additions, current lines the proposal
// drops render as removals.
function DoctrineDiff({ current, pending }: { current: string | null; pending: string }) {
  const currentLines = (current ?? '').split('\n').filter((l) => l.trim() !== '');
  const pendingLines = pending.split('\n').filter((l) => l.trim() !== '');
  const currentSet = new Set(currentLines);
  const pendingSet = new Set(pendingLines);
  const removed = currentLines.filter((l) => !pendingSet.has(l));

  return (
    <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, margin: '6px 0', padding: 8, background: 'var(--gray-bg)', borderRadius: 4 }}>
      {removed.map((l, i) => (
        <div key={`r${i}`} style={{ color: 'var(--red)' }}>- {l}</div>
      ))}
      {pendingLines.map((l, i) =>
        currentSet.has(l) ? (
          <div key={`p${i}`}>  {l}</div>
        ) : (
          <div key={`p${i}`} style={{ color: 'var(--green)' }}>+ {l}</div>
        )
      )}
    </pre>
  );
}

function ForemanDoctrine() {
  const [doctrine, setDoctrine] = useState<{ active: string | null; pending: string | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function load() {
    fetchJson<{ active: string | null; pending: string | null }>('/api/foreman/doctrine')
      .then(setDoctrine)
      .catch((e) => setError((e as Error).message));
  }

  useEffect(load, []);

  async function act(path: string) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await postJsonBody(path, {});
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (doctrine === null) {
    return error ? <div className="errors">{error}</div> : <div className="card-sub">Loading doctrine…</div>;
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div className="card-sub" style={{ marginBottom: 6 }}>Doctrine (operator-approved; hard safety rules in code always win)</div>
      {error && <div className="errors">{error}</div>}

      {doctrine.active === null ? (
        <div className="card-sub">No learned doctrine active — triage runs on hard rules only.</div>
      ) : (
        <div>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, margin: '6px 0', padding: 8, background: 'var(--gray-bg)', borderRadius: 4 }}>
            {doctrine.active}
          </pre>
          <button
            className="btn btn-outline"
            type="button"
            disabled={busy}
            onClick={() => act('/api/foreman/doctrine/clear')}
          >
            Clear
          </button>
        </div>
      )}

      {doctrine.pending !== null && (
        <div style={{ marginTop: 12 }}>
          <div className="badge-row">
            <span className="badge badge-yellow">pending doctrine — awaiting your approval</span>
          </div>
          <DoctrineDiff current={doctrine.active} pending={doctrine.pending} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-primary"
              type="button"
              disabled={busy}
              onClick={() => act('/api/foreman/doctrine/approve')}
            >
              Approve
            </button>
            <button
              className="btn btn-danger"
              type="button"
              disabled={busy}
              onClick={() => act('/api/foreman/doctrine/reject')}
            >
              Reject
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ForemanSection() {
  return (
    <div className="section">
      <h3>Foreman</h3>
      <ForemanToggle
        path="/api/settings/foreman-enabled"
        label="Foreman"
        hint="watches stuck/blocked tracks; every action is notified and reasoned"
      />
      <ForemanToggle
        path="/api/settings/foreman-auto-cancel"
        label="Auto-cancel"
        hint="acts on my behalf; every action is notified and reasoned"
      />
      <ForemanToggle
        path="/api/settings/foreman-auto-requeue"
        label="Auto-requeue"
        hint="acts on my behalf; every action is notified and reasoned"
      />
      <ForemanDoctrine />
      <ForemanDecisions />
    </div>
  );
}

function ConvergingRoundsSection() {
  return (
    <div className="section">
      <h3>Converging Review Rounds</h3>
      <ForemanToggle
        path="/api/settings/converging-remediation"
        label="Converging remediation"
        hint="off = today's fixed 2-round cap; on = keeps remediation firing while rounds are converging, resolves a rebuttal-cleared verdict to approve, and rebuilds instead of parking on a true deadlock"
      />
    </div>
  );
}

interface OAuthClientStatus {
  client_id: WebhookStatus;
  client_secret: WebhookStatus;
}

interface OAuthSettings {
  microsoft: OAuthClientStatus;
  google: OAuthClientStatus;
}

function OAuthProviderFields({
  label,
  provider,
  status,
  clientId,
  clientSecret,
  onClientId,
  onClientSecret,
}: {
  label: string;
  provider: 'microsoft' | 'google';
  status: OAuthClientStatus;
  clientId: string | null;
  clientSecret: string | null;
  onClientId: (v: string) => void;
  onClientSecret: (v: string) => void;
}) {
  const redirectUri = `${window.location.origin}/api/auth/oauth/${provider}/callback`;
  return (
    <div style={{ marginBottom: 16 }}>
      <div className="card-sub" style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>
      <div className="card-sub" style={{ marginBottom: 6 }}>
        Redirect URI to register: <span className="code">{redirectUri}</span>
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 6, maxWidth: 420 }}>
        <input
          type="text"
          placeholder={status.client_id.configured ? (status.client_id.masked ?? 'configured') : 'Client ID'}
          value={clientId ?? ''}
          onChange={(e) => onClientId(e.target.value)}
          style={{ flex: 1, minWidth: 0 }}
        />
        {status.client_id.configured && <span className="badge badge-green">set</span>}
      </div>
      <div style={{ display: 'flex', gap: 6, maxWidth: 420 }}>
        <input
          type="password"
          placeholder={status.client_secret.configured ? (status.client_secret.masked ?? 'configured') : 'Client secret'}
          value={clientSecret ?? ''}
          onChange={(e) => onClientSecret(e.target.value)}
          style={{ flex: 1, minWidth: 0 }}
        />
        {status.client_secret.configured && <span className="badge badge-green">set</span>}
      </div>
    </div>
  );
}

function OAuthSection() {
  const [settings, setSettings] = useState<OAuthSettings | null>(null);
  const [msClientId, setMsClientId] = useState<string | null>(null);
  const [msClientSecret, setMsClientSecret] = useState<string | null>(null);
  const [googleClientId, setGoogleClientId] = useState<string | null>(null);
  const [googleClientSecret, setGoogleClientSecret] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchJson<OAuthSettings>('/api/settings/oauth')
      .then(setSettings)
      .catch((e) => setError((e as Error).message));
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  async function save() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const payload: Record<string, string> = {};
      if (msClientId !== null) payload.microsoft_client_id = msClientId;
      if (msClientSecret !== null) payload.microsoft_client_secret = msClientSecret;
      if (googleClientId !== null) payload.google_client_id = googleClientId;
      if (googleClientSecret !== null) payload.google_client_secret = googleClientSecret;
      const result = await putJson<OAuthSettings>('/api/settings/oauth', payload);
      setSettings(result);
      setMsClientId(null);
      setMsClientSecret(null);
      setGoogleClientId(null);
      setGoogleClientSecret(null);
      setToast('Saved');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!settings) {
    return error ? <div className="errors">{error}</div> : <div className="empty">Loading…</div>;
  }

  return (
    <div className="section">
      <h3>Single sign-on</h3>
      {error && <div className="errors">{error}</div>}
      {toast && <div className="badge badge-green" style={{ marginBottom: 12 }}>{toast}</div>}

      <OAuthProviderFields
        label="Microsoft"
        provider="microsoft"
        status={settings.microsoft}
        clientId={msClientId}
        clientSecret={msClientSecret}
        onClientId={setMsClientId}
        onClientSecret={setMsClientSecret}
      />
      <OAuthProviderFields
        label="Google"
        provider="google"
        status={settings.google}
        clientId={googleClientId}
        clientSecret={googleClientSecret}
        onClientId={setGoogleClientId}
        onClientSecret={setGoogleClientSecret}
      />

      <button className="btn btn-primary" type="button" disabled={busy} onClick={save}>
        Save
      </button>
    </div>
  );
}

function AccountSection() {
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  async function setPassword() {
    if (busy || newPassword.length < 12) return;
    setBusy(true);
    setError(null);
    try {
      await putJson('/api/auth/password', { new_password: newPassword });
      setNewPassword('');
      setToast('Password updated');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="section">
      <h3>Account</h3>
      {error && <div className="errors">{error}</div>}
      {toast && <div className="badge badge-green" style={{ marginBottom: 12 }}>{toast}</div>}
      <div className="card-sub" style={{ marginBottom: 8 }}>
        Set or change your email+password login credential (12+ characters). Requires signing in with your account
        first — the shared operator password can&apos;t be used to set this.
      </div>
      <div style={{ display: 'flex', gap: 6, maxWidth: 380 }}>
        <input
          type="password"
          placeholder="New password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          style={{ flex: 1, minWidth: 0 }}
        />
        <button className="btn btn-outline" type="button" disabled={busy || newPassword.length < 12} onClick={setPassword}>
          Set password
        </button>
      </div>
    </div>
  );
}

export default function Settings() {
  return (
    <div>
      <AccountSection />
      <OAuthSection />
      <NotificationsSection />
      <EngineeringSection />
      <ForemanSection />
      <ConvergingRoundsSection />
    </div>
  );
}
